import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Calendar, ChevronRight } from 'lucide-react';
import FavouriteButton from '@/components/showcase/FavouriteButton';
import ShareButtons from '@/components/shop/ShareButtons';

export default function ShowcaseHero({ rig }) {
  const [videoError, setVideoError] = useState(false);
  const rating = Number(rig.overall_rating || 0);
  const bookingUrl = rig.booking_url || '/bookings';

  return (
    <section className="relative min-h-[88vh] flex flex-col justify-end overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        {rig.hero_video_url && !videoError ? (
          <video
            autoPlay
            muted
            loop
            playsInline
            poster={rig.hero_image_url}
            onError={() => setVideoError(true)}
            className="w-full h-full object-cover"
          >
            <source src={rig.hero_video_url} type="video/mp4" />
          </video>
        ) : rig.hero_image_url ? (
          <img src={rig.hero_image_url} alt={rig.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full carbon-bg" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0f] via-[#0d0d0f]/60 to-[#0d0d0f]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0f]/80 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full pt-32 pb-16">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="flex items-center gap-3 mb-5">
            <FavouriteButton rigId={rig.id} className="relative bg-white/5 border-white/10" />
            <ShareButtons title={rig.name} />
          </div>

          <div className="inline-flex items-center gap-2 border border-primary/40 bg-primary/10 text-primary text-[10px] font-bold tracking-widest px-3 py-1.5 mb-6">
            <Star className="w-3 h-3 fill-primary" />
            PROFESSIONAL SIMULATOR
          </div>

          <h1 className="font-black uppercase text-white leading-[0.92] mb-5" style={{ fontSize: 'clamp(2.5rem, 7vw, 5.5rem)' }}>
            {rig.name}
          </h1>

          {rig.short_description && (
            <p className="text-white/60 text-base md:text-lg max-w-2xl leading-relaxed mb-8">
              {rig.short_description}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-6">
            {rating > 0 && (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <Star className="w-5 h-5 text-primary fill-primary" />
                  <span className="text-white font-black text-2xl">{rating.toFixed(1)}</span>
                  <span className="text-white/40 text-sm">/10</span>
                </div>
                <span className="text-white/30 text-xs tracking-widest font-bold">OVERALL RATING</span>
              </div>
            )}

            <Link to={bookingUrl}>
              <button className="group inline-flex items-center gap-2.5 bg-primary hover:bg-primary/90 text-white font-black text-xs tracking-widest px-8 py-4 transition-colors">
                <Calendar className="w-4 h-4" /> BOOK THIS RIG
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}